import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";
import { normalizeNfc } from "../../utils/text";
import { projectsApi, type TaskBoardAccess } from "./projects";

// ── 型定義（BE: docs/api/producer-task-board.md に基づく） ──────────────
//
// 1 プロジェクトに複数枚のボード（タブ）を持てる。`board_id` を省略した
// エンドポイントは常に主ボード（sort_order 先頭）を指すので、旧来の
// 単一ボード前提の呼び出しもそのまま動く。

/** 表の列定義（順序付き） */
export interface TaskColumn {
  id: string;
  title: string;
  order: number;
  /** true なら左隣の列の完了を待たず同時進行できる（既定 false = 直列）。列単位で設定 */
  parallel: boolean;
}

/** 1 セルの内容。content は Markdown */
export interface TaskCell {
  content: string;
}

/** タスク（行）。cells のキーは列 ID */
export interface ProducerTask {
  id: string;
  title: string;
  cells: Record<string, TaskCell>;
  /** セル本文の最遅日付（YYYY-MM-DD）。並び順の基準として FE が算出するデノーマライズ値 */
  deadline: string | null;
  sort_order: number;
  created: string | null;
  modified: string | null;
}

export interface TaskBoard {
  id: string;
  project_id: string;
  /** タブに出すボード名（BE が既定ラベルへフォールバック済み） */
  name: string;
  /** タブの並び順（0 が主ボード） */
  sort_order: number;
  columns: TaskColumn[];
  tasks: ProducerTask[];
  /** ディレクターの共有アクセスレベル（責任者プロデューサーが設定） */
  director_access: TaskBoardAccess;
  /** クライアント責任者の共有アクセスレベル */
  client_access: TaskBoardAccess;
  /** このボードに対する閲覧者の有効アクセス（一覧 API のみ付与） */
  access?: TaskBoardAccess;
}

export interface TaskBoardResponse {
  status: string;
  data: {
    board: TaskBoard;
    /** 閲覧者の有効アクセス（GET のみ返る。読み取り専用判定用） */
    access?: TaskBoardAccess;
    /** 共有設定を変更できるか（GET のみ返る。責任者プロデューサー・管理者のみ true） */
    can_manage_access?: boolean;
  };
}

export interface TaskBoardsResponse {
  status: string;
  data: {
    /** 閲覧できるボードを sort_order 順に全件（列・タスク込み） */
    boards: TaskBoard[];
    /** ボードを横断した最も強いアクセス（画面の出し分け用） */
    access?: TaskBoardAccess;
    can_manage_access?: boolean;
    /** 1 プロジェクトのボード数上限。「+」チップの出し分けに使う（旧 BE は未返却） */
    max_boards?: number;
  };
}

export interface SingleTaskResponse {
  status: string;
  data: { task: ProducerTask };
}

/** 行移動の応答。移動後のタスクと移動先ボード（列追加があれば反映済み） */
export interface MoveTaskResponse {
  status: string;
  data: { task: ProducerTask; board: TaskBoard };
}

/** アクティビティ集計の元データ。ボード 1 枚ぶん（共有設定などは持たない） */
export interface ActivitySourceBoard {
  id: string;
  name: string;
  columns: TaskColumn[];
  /** 作業時間つきメンション（`@山田[3h]`）を含むタスクだけ */
  tasks: ProducerTask[];
}

export interface ActivitySourceProject {
  project_id: string;
  code_name: string;
  banner_color: string | null;
  boards: ActivitySourceBoard[];
}

export interface ActivitySourceResponse {
  status: string;
  data: {
    /** 参加中の全プロジェクト（進行中・完了を問わない）のうち実績があるもの */
    projects: ActivitySourceProject[];
  };
}

// ── リクエストパラメータ ──────────────────────────────────────────────

/** `board_id` を省略すると主ボードが対象になる（全エンドポイント共通）。 */
interface BoardScopedParams {
  project_id: string;
  board_id?: string;
}

export interface GetTaskBoardsParams {
  project_id: string;
}

/** 列の全置換。id 省略時はサーバが採番、order は配列順で再採番される */
export interface UpdateColumnsParams extends BoardScopedParams {
  columns: { id?: string; title: string; parallel?: boolean }[];
}

export interface CreateTaskParams extends BoardScopedParams {
  title?: string;
  cells?: Record<string, TaskCell>;
  deadline?: string | null;
}

export interface UpdateTaskParams extends BoardScopedParams {
  task_id: string;
  title?: string;
  cells?: Record<string, TaskCell>;
  deadline?: string | null;
  sort_order?: number;
}

export interface DeleteTaskParams extends BoardScopedParams {
  task_id: string;
}

/**
 * 行の別ボードへの移動（同一プロジェクト内限定）。
 * セルの列対応はタイトル照合で BE が行う。移動先に無い列があるときは
 * `add_missing_columns: true` を付けない限り 409（missing_columns 付き）が返る。
 */
export interface MoveTaskParams {
  project_id: string;
  /** 移動元ボード（move は board_id 必須のパスのみ） */
  board_id: string;
  task_id: string;
  target_board_id: string;
  /** 不足列を移動先の末尾へ追加してよいか（確認モーダル承諾後に true） */
  add_missing_columns?: boolean;
}

/** 共有設定（ポジション単位）の更新。責任者プロデューサー・管理者のみ許可される */
export interface UpdateTaskBoardAccessParams extends BoardScopedParams {
  director_access?: TaskBoardAccess;
  client_access?: TaskBoardAccess;
}

export interface CreateTaskBoardParams {
  project_id: string;
  /** 省略時は「ボードN」が自動で付く */
  name?: string;
}

export interface UpdateTaskBoardParams {
  project_id: string;
  board_id: string;
  name?: string;
  sort_order?: number;
}

export interface DeleteTaskBoardParams {
  project_id: string;
  board_id: string;
}

export interface ReorderTaskBoardsParams {
  project_id: string;
  /** 新しい並び順。プロジェクトの全ボード ID を過不足なく渡す。 */
  board_ids: string[];
}

/** セル添付 1 件の変換結果（MediaConvertJob）。 */
export interface TaskBoardMediaInfo {
  /** COMPLETE / PROCESSING / FAILED など（Lambda が書く値） */
  status: string;
  /** 動画の先頭フレーム / PDF の 1 ページ目 */
  thumbnail_url: string | null;
  /** 画像の最適化版（WebP） */
  image_url: string | null;
}

export interface TaskBoardMediaInfoParams {
  project_id: string;
  /** `{project_id}/task_board/...` の S3 キー（キャッシュキーが揺れないよう整列済みで渡す） */
  keys: string[];
}

export interface TaskBoardMediaInfoResponse {
  status: string;
  data: {
    /** S3 キー → 変換結果。変換ジョブが無いキーは含まれない */
    items: Record<string, TaskBoardMediaInfo>;
  };
}

/** 作成/更新 body の title を NFC 正規化して表記ゆれ（NFD/NFC）を吸収する。 */
const normalizeTaskWriteBody = <T extends { title?: string }>(body: T): T =>
  body.title === undefined
    ? body
    : ({ ...body, title: normalizeNfc(body.title) } as T);

/** ボード配下のパス。board_id があれば複数ボード用のルートを使う。 */
const boardPath = (projectId: string, boardId?: string) =>
  boardId
    ? `/api/${projectId}/task-boards/${boardId}`
    : `/api/${projectId}/task-board`;

/**
 * 内容が変わる操作の無効化タグ。プロジェクト単位のキャッシュに加えて、
 * 全プロジェクトを 1 本のキャッシュに畳んでいるアクティビティ集計も落とす。
 */
const contentTags = (projectId: string) => [
  { type: "TaskBoard" as const, id: projectId },
  { type: "TaskBoard" as const, id: "activity-source" },
];

export const taskBoardApi = createApi({
  reducerPath: "taskBoardApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["TaskBoard"],
  endpoints: (builder) => ({
    /**
     * プロジェクトの全ボード（列・タスク込み）。タブ表示と横断ガントの供給源。
     * 単一ボードだけを取る GET は使っていない（1 リクエストで全ボードを取り、
     * 表もガントもこのキャッシュを共有する）。
     * プロジェクト単位でタグを管理し、無効化を他プロジェクトに波及させない。
     */
    getTaskBoards: builder.query<TaskBoardsResponse, GetTaskBoardsParams>({
      query: ({ project_id }) => `/api/${project_id}/task-boards`,
      providesTags: (_result, _error, arg) => [
        { type: "TaskBoard" as const, id: arg.project_id },
      ],
    }),

    /**
     * アクティビティ集計（`/taskboard?view=activity`）の元データを 1 リクエストで取得する。
     *
     * 参加中の全プロジェクト（進行中・完了を問わない）を対象に、作業時間つき
     * メンションを含むタスクだけが返る。プロジェクトごとに `getTaskBoards` を
     * 叩くとリクエスト数がプロジェクト数に比例するため、集計ページはこちらを使う。
     * タスクの更新でも無効化されるよう、横断タグ（id: "activity-source"）を持つ。
     */
    getTaskBoardActivitySource: builder.query<ActivitySourceResponse, void>({
      query: () => `/api/task-boards/activity-source`,
      providesTags: [{ type: "TaskBoard" as const, id: "activity-source" }],
    }),

    /**
     * セル添付の変換結果（サムネイル / 最適化画像）を S3 キーでまとめて引く。
     * ボード本体の取得・同期とは切り離し、本文中の添付キーが変わったときだけ
     * 引き直す（タスク更新のタグ無効化では再取得しない）。
     */
    getTaskBoardMediaInfo: builder.query<
      TaskBoardMediaInfoResponse,
      TaskBoardMediaInfoParams
    >({
      query: ({ project_id, keys }) => ({
        url: `/api/${project_id}/task-board/media-info`,
        method: "POST",
        body: { keys },
      }),
    }),

    createTaskBoard: builder.mutation<TaskBoardResponse, CreateTaskBoardParams>({
      query: ({ project_id, name }) => ({
        url: `/api/${project_id}/task-boards`,
        method: "POST",
        body: name ? { name } : {},
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "TaskBoard" as const, id: arg.project_id },
      ],
    }),

    /** ボード名・並び順の更新（列や共有設定は専用の mutation を使う）。 */
    updateTaskBoard: builder.mutation<TaskBoardResponse, UpdateTaskBoardParams>({
      query: ({ project_id, board_id, ...body }) => ({
        url: `/api/${project_id}/task-boards/${board_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "TaskBoard" as const, id: arg.project_id },
      ],
    }),

    /** タブの D&D 並び替え。1 リクエストで全ボードの sort_order を振り直す。 */
    reorderTaskBoards: builder.mutation<
      TaskBoardsResponse,
      ReorderTaskBoardsParams
    >({
      query: ({ project_id, board_ids }) => ({
        url: `/api/${project_id}/task-boards`,
        method: "PATCH",
        body: { board_ids },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "TaskBoard" as const, id: arg.project_id },
      ],
      // 並び替えはドラッグ直後に見た目が確定していてほしいので、
      // 応答を待たずキャッシュ上の順序を入れ替える（失敗時は巻き戻す）。
      async onQueryStarted(
        { project_id, board_ids },
        { dispatch, queryFulfilled },
      ) {
        const patchResult = dispatch(
          taskBoardApi.util.updateQueryData(
            "getTaskBoards",
            { project_id },
            (draft) => {
              const byId = new Map(draft.data.boards.map((b) => [b.id, b]));
              const reordered = board_ids
                .map((id) => byId.get(id))
                .filter((b): b is TaskBoard => b !== undefined);
              if (reordered.length === draft.data.boards.length) {
                draft.data.boards = reordered;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),

    deleteTaskBoard: builder.mutation<
      { status: string; message: string },
      DeleteTaskBoardParams
    >({
      query: ({ project_id, board_id }) => ({
        url: `/api/${project_id}/task-boards/${board_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => contentTags(arg.project_id),
    }),

    updateColumns: builder.mutation<TaskBoardResponse, UpdateColumnsParams>({
      query: ({ project_id, board_id, columns }) => ({
        url: boardPath(project_id, board_id),
        method: "PATCH",
        body: { columns },
      }),
      // 列構成の更新後はキャッシュを無効化し、他端末や別タブの状態と整合させる。
      // 楽観的更新（呼び出し側のローカル state）と返答後の再取得が二重になるが、
      // FE 側の「キー比較マージ」で同値ならローカル state を上書きしない設計のため
      // ちらつきは起きない。
      invalidatesTags: (_result, _error, arg) => contentTags(arg.project_id),
    }),

    createTask: builder.mutation<SingleTaskResponse, CreateTaskParams>({
      query: ({ project_id, board_id, ...body }) => ({
        url: `${boardPath(project_id, board_id)}/tasks`,
        method: "POST",
        body: normalizeTaskWriteBody(body),
      }),
      invalidatesTags: (_result, _error, arg) => contentTags(arg.project_id),
    }),

    updateTask: builder.mutation<SingleTaskResponse, UpdateTaskParams>({
      query: ({ project_id, board_id, task_id, ...body }) => ({
        url: `${boardPath(project_id, board_id)}/tasks/${task_id}`,
        method: "PATCH",
        body: normalizeTaskWriteBody(body),
      }),
      invalidatesTags: (_result, _error, arg) => contentTags(arg.project_id),
    }),

    deleteTask: builder.mutation<{ status: string; message: string }, DeleteTaskParams>({
      query: ({ project_id, board_id, task_id }) => ({
        url: `${boardPath(project_id, board_id)}/tasks/${task_id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => contentTags(arg.project_id),
    }),

    /** 行を同一プロジェクト内の別ボードへ移動する（行メニュー「行を移動」）。 */
    moveTask: builder.mutation<MoveTaskResponse, MoveTaskParams>({
      query: ({
        project_id,
        board_id,
        task_id,
        target_board_id,
        add_missing_columns,
      }) => ({
        url: `/api/${project_id}/task-boards/${board_id}/tasks/${task_id}/move`,
        method: "POST",
        body: {
          target_board_id,
          ...(add_missing_columns ? { add_missing_columns: true } : {}),
        },
      }),
      // 移動元・移動先の 2 ボードが変わるので、プロジェクト単位でまとめて落とす。
      invalidatesTags: (_result, _error, arg) => contentTags(arg.project_id),
    }),

    updateTaskBoardAccess: builder.mutation<
      TaskBoardResponse,
      UpdateTaskBoardAccessParams
    >({
      query: ({ project_id, board_id, ...body }) => ({
        url: boardPath(project_id, board_id),
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "TaskBoard" as const, id: arg.project_id },
      ],
      // 一覧/詳細の task_board_access は projectsApi 側のキャッシュにあるため、
      // 別 slice のタグも無効化して横断ボードのフィルタ・設定UI に反映する。
      async onQueryStarted({ project_id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            projectsApi.util.invalidateTags([
              { type: "Projects", id: project_id },
              { type: "Projects", id: "LIST" },
            ]),
          );
        } catch {
          // 失敗時は何もしない（taskBoard 側の invalidatesTags で再取得される）
        }
      },
    }),
  }),
});

export const {
  useGetTaskBoardsQuery,
  useGetTaskBoardActivitySourceQuery,
  useGetTaskBoardMediaInfoQuery,
  useCreateTaskBoardMutation,
  useUpdateTaskBoardMutation,
  useReorderTaskBoardsMutation,
  useDeleteTaskBoardMutation,
  useUpdateColumnsMutation,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useMoveTaskMutation,
  useUpdateTaskBoardAccessMutation,
} = taskBoardApi;
